import { env } from '@/env';
import type { Translations } from '@/i18n/locales/en';
import { copyText } from '@/lib/clipboard';
import { showCopyFallbackDialog } from '@/lib/copy-fallback-dialog';
import { ListItem } from 'framework7-react';

type PrintCopy = Translations['print'];

// The print-service (apps/print-service, :3119) hosts the Epson device routes.
// Trim a trailing slash so we build clean `…/epson/*` URLs. These two URLs are
// the SAME for every printer - they are the operator's Server Direct Print
// endpoints - so they live here and are shown once, always, on the printers
// list (the way In-House presents them) rather than being buried per-printer.
const PRINT_BASE = env.printUrl.replace(/\/+$/, '');
export const PRINT_URL = PRINT_BASE ? `${PRINT_BASE}/epson/direct-print` : null;
export const NOTIFICATION_URL = PRINT_BASE ? `${PRINT_BASE}/epson/notification` : null;

export const copy = async (value: string, label: string, tp: PrintCopy) => {
  // Copy to the clipboard (Clipboard API → legacy execCommand fallback that
  // works over insecure http, the operator tablets), then always show the value
  // in a dialog with an OK button so the operator can read/verify it and copy by
  // hand if the automatic copy didn't stick.
  await copyText(value);
  showCopyFallbackDialog(value, label, tp.okButton);
};

// A read-only "copy this value into the printer" row: label on top, the full
// value wrapping below (URLs are long - never truncate a real value), tinted
// Copy button on the right. A plain function returning a ListItem so F7 sees the
// row type directly (Rule 9 - never a wrapper component / JSX).
export function copyRow(tp: PrintCopy, label: string, value: string) {
  return (
    <ListItem key={label} title={label}>
      <div
        slot="subtitle"
        style={{
          fontFamily: 'monospace',
          whiteSpace: 'normal',
          overflowWrap: 'anywhere',
          userSelect: 'all',
        }}
      >
        {value}
      </div>
      {/* HIG tinted button - matches the reservation-setup "Copy" action so the
          Copy IS the control (not the whole row). */}
      <button
        slot="after"
        type="button"
        className="rsv-copy-btn"
        onClick={() => copy(value, label, tp)}
      >
        <i className="f7-icons">doc_on_doc</i>
        {tp.copyButton}
      </button>
    </ListItem>
  );
}
